import React, { useState, useEffect } from 'react';
import { 
    UserCircle, Calendar, LogOut, Phone, Layers, MapPin, 
    Clock, CreditCard, Building, Hash, Banknote, ShieldCheck, Briefcase,
    Type, Palette, Baseline, Volume2, Image as ImageIcon, Bell, LayoutTemplate, Zap, Edit2
} from 'lucide-react';

// --- IMPORT NEW GLOBAL UI COMPONENTS ---
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Tabs from '../../ui/Tabs';
import IconBox from '../../ui/IconBox';
import Input from '../../ui/Input';

// --- LOCALIZED MALEGAON & INDIAN BANKS SMART LIST ---
const INDIAN_BANKS = [
    { name: "Malegaon Merchants Co-operative Bank", ifsc: "IBKL0533MMC" }, 
    { name: "The Malegaon Co-operative Bank Ltd", ifsc: "IBKL0533MMC" }, 
    { name: "Nashik District Central Co-op Bank (NDCC)", ifsc: "ICIC00NDCCB" }, 
    { name: "Janata Sahakari Bank (Malegaon)", ifsc: "HDFC0CJBMLG" }, 
    { name: "Shirpur Peoples Co-op Bank", ifsc: "KKBK0SPCB01" }, 
    { name: "Nashik Rd Deolali Bank (Malegaon)", ifsc: "YESB0NRDB28" },
    { name: "Janalaxmi Coop Bank (Malegaon)", ifsc: "YESB0JLCB24" },
    { name: "Nashik Jilha Mahila Bank (Malegaon)", ifsc: "YESB0NMVB07" },
    { name: "State Bank of India (Malegaon)", ifsc: "SBIN0000418" },
    { name: "Bank of Maharashtra (Malegaon)", ifsc: "MAHB0000278" },
    { name: "HDFC Bank (Malegaon)", ifsc: "HDFC0002556" },
    { name: "ICICI Bank (Malegaon)", ifsc: "ICIC0006428" },
    { name: "Axis Bank (Malegaon)", ifsc: "UTIB0001240" },
    { name: "Bank of Baroda (Malegaon Camp)", ifsc: "BARB0DBMCAM" },
    { name: "Bank of Baroda (Malegaon City)", ifsc: "BARB0DBMLCT" },
    { name: "Union Bank of India (Malegaon)", ifsc: "UBIN0538787" },
    { name: "Punjab National Bank (Malegaon)", ifsc: "PUNB0785800" },
    { name: "Central Bank of India (Malegaon)", ifsc: "CBIN0280705" },
    { name: "Bank of India (Malegaon)", ifsc: "BKID0000813" },
    { name: "Kotak Mahindra Bank (Soygaon)", ifsc: "KKBK0000699" },
    { name: "Kotak Mahindra Bank (Chandanpuri)", ifsc: "KKBK0001891" },
    { name: "IndusInd Bank (Malegaon)", ifsc: "INDB0001593" },
    { name: "Canara Bank (Malegaon Somwarpeth)", ifsc: "CNRB0015281" },
    { name: "Yes Bank (Dahiwal, Malegaon)", ifsc: "YESB0001091" }, 
    { name: "Indian Bank (Nasik City)", ifsc: "IDIB000N016" },  
    { name: "UCO Bank (Girnare)", ifsc: "UCBA0001521" }, 
    { name: "IDFC First Bank (Nashik - Mico Circle)", ifsc: "IDFB0042281" }, 
    { name: "IDFC First Bank (Nashik - Gangapur Road)", ifsc: "IDFB0042283" }, 
    { name: "IDFC First Bank (Pimpalgaon Baswant)", ifsc: "IDFB0042286" }, 
    { name: "IDFC First Bank (Nashik - Deolali Road)", ifsc: "IDFB0042289" }, 
    { name: "Airtel Payments Bank", ifsc: "AIRP0000001" },
    { name: "India Post Payments Bank", ifsc: "IPOS0000001" }
];

// --- TIME FORMATTER ---
const formatTimeForDisplay = (t) => {
    if (!t) return "";
    let timeStr = String(t).trim();
    timeStr = timeStr.replace(/^\d{4}-\d{2}-\d{2}\s+/, ''); 
    if (timeStr.toUpperCase().includes('PM') || timeStr.toUpperCase().includes('AM')) return timeStr;
    let parts = timeStr.split(':');
    let h = parseInt(parts[0], 10) || 0;
    let m = parts.length > 1 ? parts[1] : '00';
    const ampm = h >= 12 ? 'PM' : 'AM';
    let hr = h % 12 || 12;
    return `${hr}:${m} ${ampm}`;
};

// --- HINDI VOICE TIME PARSER & DURATION CALCULATOR ---
const getHindiTimeText = (t) => {
    if(!t) return "";
    let timeStr = String(t).trim().replace(/^\d{4}-\d{2}-\d{2}\s+/, ''); 
    let parts = timeStr.split(':');
    let h = parseInt(parts[0], 10) || 0;
    let m = parseInt(parts.length > 1 ? parts[1] : 0, 10);
    
    let isPM = timeStr.toUpperCase().includes('PM');
    let isAM = timeStr.toUpperCase().includes('AM');
    
    if (isPM && h !== 12) h += 12;
    if (isAM && h === 12) h = 0;
    
    let period = "दिन के";
    if (h >= 19 || h <= 3) period = "रात के";
    else if (h >= 4 && h <= 11) period = "सुबह के";
    else if (h >= 12 && h <= 15) period = "दोपहर के";
    else if (h >= 16 && h <= 18) period = "शाम के";
    
    let hr12 = h % 12 || 12;
    let minText = m > 0 ? ` बज कर ${m} मिनट` : ` बजे`;
    return `${period} ${hr12}${minText}`;
};

const getShiftVoiceMsg = (user) => {
    const shiftName = user.shift || 'तय नहीं';
    if (!user.shift_start || !user.shift_end) {
        return `आपको ${shiftName} शिफ्ट असाइन की गई है, जिसका समय अभी तय नहीं है।`;
    }

    const startHindi = getHindiTimeText(user.shift_start);
    const endHindi = getHindiTimeText(user.shift_end);
    // Add this line with other derived variables (around line 145)


    let timeStrStart = String(user.shift_start).trim().replace(/^\d{4}-\d{2}-\d{2}\s+/, ''); 
    let timeStrEnd = String(user.shift_end).trim().replace(/^\d{4}-\d{2}-\d{2}\s+/, ''); 
    
    let startH = parseInt(timeStrStart.split(':')[0], 10) || 0;
    let startM = parseInt(timeStrStart.split(':')[1], 10) || 0;
    let endH = parseInt(timeStrEnd.split(':')[0], 10) || 0;
    let endM = parseInt(timeStrEnd.split(':')[1], 10) || 0;

    let isPMStart = timeStrStart.toUpperCase().includes('PM');
    if (isPMStart && startH !== 12) startH += 12;
    let isAMStart = timeStrStart.toUpperCase().includes('AM');
    if (isAMStart && startH === 12) startH = 0;

    let isPMEnd = timeStrEnd.toUpperCase().includes('PM');
    if (isPMEnd && endH !== 12) endH += 12;
    let isAMEnd = timeStrEnd.toUpperCase().includes('AM');
    if (isAMEnd && endH === 12) endH = 0;

    let totalStartMins = startH * 60 + startM;
    let totalEndMins = endH * 60 + endM;
    
    if (totalEndMins < totalStartMins) {
        totalEndMins += 24 * 60; // Crossed midnight
    }
    
    let durationMins = totalEndMins - totalStartMins;
    let durH = Math.floor(durationMins / 60);
    let durM = durationMins % 60;
    
    let durationText = `${durH} घंटे`;
    if (durM > 0) durationText += ` और ${durM} मिनट`;

    return `आपको ${shiftName} शिफ्ट असाइन की गई है, जिसका टाइम है, ${startHindi} से लेकर ${endHindi} तक, टोटल ${durationText} की ड्यूटी है।`;
};

// --- LOOM PARSER ---
const getLoomNumber = (id) => {
    if (!id) return 0;
    const str = String(id);
    const digits = str.replace(/[^0-9]/g, '');
    return digits ? parseInt(digits, 10) : 0;
};

const formatSimpleLoomList = (loomStr) => {
    if (!loomStr) return "Not Assigned";
    const str = Array.isArray(loomStr) ? loomStr.join(',') : String(loomStr);
    const nums = [...new Set(str.split(',').map(s => getLoomNumber(s)).filter(n => n > 0).sort((a, b) => a - b))];
    return nums.length > 0 ? nums.join(', ') : "None";
};

// --- SMART DATE PARSER ---
const parseSafeDate = (dateStr) => {
    if (!dateStr || String(dateStr).startsWith("0000")) return "Not provided";
    
    // Hyphens ko forward slashes se replace kiya taaki local time read ho
    let d = new Date(String(dateStr).replace(/-/g, '/')); 
    if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-IN', {day: 'numeric', month: 'long', year: 'numeric'});
    }
    
    if (typeof dateStr === 'string') {
        const parts = dateStr.split(/[-/]/);
        if (parts.length === 3) {
            if (parts[2].length === 4) {
                d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                if (!isNaN(d.getTime())) {
                    return d.toLocaleDateString('en-IN', {day: 'numeric', month: 'long', year: 'numeric'});
                }
            }
        }
    }
    return dateStr;
};

// --- COMPONENT: PROFILE ROW ---
const ProfileRow = ({ icon: Icon, label, value, onClick }) => (
    <div 
        onClick={onClick} 
        style={{display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '16px 4px', borderBottom: '1px solid rgba(241, 245, 249, 0.8)', cursor: onClick ? 'pointer' : 'default', transition: 'all 0.2s ease'}}
    >
        {/* Simple Plain Icon */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }}>
            <Icon size={18} color="#64748B" strokeWidth={2.5} />
        </div>
        
        <div style={{flex: 1}}>
            <span style={{fontSize: '9px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px'}}>{label}</span>
            <div style={{fontSize: '13px', color: '#0F172A', fontWeight: '900', marginTop: '2px', wordBreak: 'break-word'}}>
                {value || <span style={{color: '#94A3B8', fontStyle: 'italic'}}>Not provided</span>}
            </div>
        </div>
    </div>
);

// --- MAIN PROFILE VIEW ---
const WorkerProfileView = ({ user, workersData, onLogout, appBg, handleVoiceClick }) => {
    
    const [activeTab, setActiveTab] = useState('profile'); 

   // 🚀 NEW: SMART PROFILE FORM DATA & STATES
    const [editMode, setEditMode] = useState({ personal: false, bank: false });
    const toggleEdit = (section) => setEditMode(prev => ({ ...prev, [section]: !prev[section] }));

    const [formData, setFormData] = useState({
        name: user.name || '',
        mobile: user.mobile || '',
        address: user.address || '',
        aadhaar: user.aadhaar || '',
        bank_name: user.bank_name || '',
        bank_account: user.bank_account || '',
        ifsc_code: user.ifsc || '',
        upi_id: user.upi_id || ''
    });

    useEffect(() => {
    if (user) {
        setFormData({
            name: user.name || '',
            mobile: user.mobile || '',
            address: user.address || '',
            aadhaar: user.aadhaar || '',
            bank_name: user.bank_name || '',
            bank_account: user.bank_account || '',
            ifsc_code: user.ifsc || user.ifsc_code || '',
            upi_id: user.upi_id || ''
        });
    }
    }, [user]);

    const [ifscStatus, setIfscStatus] = useState(user.ifsc && user.ifsc.length === 11 ? 'valid' : 'idle');
    const [showIfscSearch, setShowIfscSearch] = useState(false);
    const [locState, setLocState] = useState('');
    const [locDistrict, setLocDistrict] = useState('');

    // 👇 NEW: DATABASE CONNECTION & SAVE FUNCTION 👇
    const [isSaving, setIsSaving] = useState(false);

    const handleSaveProfile = async () => {
        setIsSaving(true);
        const submitData = new FormData();
        
        // 1. My Details (Editable)
        submitData.append('id', user.id);
        submitData.append('name', formData.name || '');
        submitData.append('mobile', formData.mobile || '');
        submitData.append('address', formData.address || '');
        submitData.append('aadhaar', formData.aadhaar || '');
        
        // 2. Work Details (Read-only for worker, keeping original values)
        submitData.append('shift', user.shift || '');
        submitData.append('looms', user.looms || user.assigned_looms || '');
        
        // SAFE RATE PARSING FOR API
        const apiPendingRate = parseFloat(user.pending_rate) || 0;
        const apiStandardRate = parseFloat(user.rate_per_meter) || parseFloat(user.ratePerMeter) || parseFloat(user.rate) || 0;
        submitData.append('rate_per_meter', apiPendingRate > 0 ? apiPendingRate : apiStandardRate);
        
        submitData.append('is_badli', user.is_badli || 0);
        submitData.append('shift_start', user.shift_start || '08:00');
        submitData.append('shift_end', user.shift_end || '20:00');
        submitData.append('doj', user.doj || '');
        
        // 3. Bank Details (Editable)
        submitData.append('bank_account', formData.bank_account || '');
        submitData.append('ifsc', formData.ifsc_code || '');
        submitData.append('bank_name', formData.bank_name || '');
        submitData.append('upi_id', formData.upi_id || '');

        try {
            const res = await fetch(`https://mukadampro.shirpur.store/update_worker.php`, {
                method: 'POST',
                body: submitData
            });
            const data = await res.json();
            if (data.status === 'success') {
                if (handleVoiceClick) handleVoiceClick(null, "प्रोफाइल डिटेल्स सफलतापूर्वक सेव हो गई हैं।");
                setEditMode({ personal: false, bank: false });
            } else {
                alert("Save Failed: " + data.message);
            }
        } catch (error) {
            alert("Network Error! Check your internet connection.");
        }
       setIsSaving(false);
    };

    const handleIfscChange = async (val) => {
        const code = val.toUpperCase().replace(/[^A-Z0-9]/g, '');
        setFormData(prev => ({ ...prev, ifsc_code: code }));

        if (code.length === 11) {
            setIfscStatus('loading');
            try {
                const res = await fetch(`https://ifsc.razorpay.com/${code}`);
                if (res.ok) {
                    const data = await res.json();
                    setFormData(prev => ({ ...prev, ifsc_code: code, bank_name: prev.bank_name ? prev.bank_name : data.BANK }));
                    setIfscStatus('valid');
                    if(handleVoiceClick) handleVoiceClick(null, `${data.BANK} वेरीफाई हो गया है।`);
                } else {
                    setIfscStatus('invalid');
                }
            } catch (err) { setIfscStatus('invalid'); }
        } else {
            setIfscStatus('idle');
        }
    };

    const inputStyle = { width: '100%', padding: '14px', borderRadius: '14px', border: '1px solid #e2e8f0', fontSize: '13px', outline: 'none', background: '#f8fafc', color:'#0F172A', fontWeight:'700', boxSizing: 'border-box', transition: 'all 0.2s ease' };

    // --- APP SETTINGS STATES ---
    const [appFont, setAppFont] = useState(localStorage.getItem('appFont') || 'Plus Jakarta Sans');
    const [appTextSize, setAppTextSize] = useState(parseInt(localStorage.getItem('appTextSize')) || 12);
    const [appTextColor, setAppTextColor] = useState(localStorage.getItem('appTextColor') || '#111111');
    const [voiceSpeed, setVoiceSpeed] = useState(parseFloat(localStorage.getItem('voiceSpeed')) || 1.0);
    const [selectedVoiceURI, setSelectedVoiceURI] = useState(localStorage.getItem('selectedVoiceURI') || '');
    const [availableVoices, setAvailableVoices] = useState([]);
    const [localAppBg, setLocalAppBg] = useState(appBg || localStorage.getItem('appBg') || "url('/OHOME.jpg') center / cover no-repeat fixed");
    const [notifOn, setNotifOn] = useState(() => {
        const saved = localStorage.getItem('notifOn');
        return saved !== null ? JSON.parse(saved) : true;
    });

    useEffect(() => {
        const fetchVoices = () => {
            const voices = window.speechSynthesis.getVoices();
            const hindiVoices = voices.filter(v => v.lang.includes('hi') || v.lang.includes('IN')).slice(0, 5);
            setAvailableVoices(hindiVoices);
        };
        fetchVoices();
        window.speechSynthesis.onvoiceschanged = fetchVoices;
    }, []);

    // Configuration Options
    const fonts = ['Plus Jakarta Sans', 'Poppins', 'Inter', 'Outfit', 'Quicksand', 'Montserrat', 'Raleway', 'Nunito', 'Playfair Display', 'Fira Sans'];
    const textColors = [
        { id: 'black', hex: '#111111', name: 'Midnight Black' }, { id: 'navy', hex: '#1E3A8A', name: 'Royal Navy' },
        { id: 'emerald', hex: '#064E3B', name: 'Deep Emerald' }, { id: 'slate', hex: '#334155', name: 'Slate Grey' },
        { id: 'crimson', hex: '#881337', name: 'Crimson Red' }, { id: 'white', hex: '#FFFFFF', name: 'Pure White' },
        { id: 'cyan', hex: '#00E5FF', name: 'Neon Cyan' }, { id: 'hotpink', hex: '#FF1493', name: 'Hot Pink' },
        { id: 'gold', hex: '#FFB800', name: 'Glowing Gold' }
    ];
    const wallpapers = [
        { id: 1, color: "url('/1.jpg') center / cover no-repeat fixed", name: 'Wallpaper 1' },
        { id: 2, color: "url('/2.jpg') center / cover no-repeat fixed", name: 'Wallpaper 2' },
        { id: 3, color: "url('/3.jpg') center / cover no-repeat fixed", name: 'Wallpaper 3' },
        { id: 4, color: "url('/4.jpg') center / cover no-repeat fixed", name: 'Wallpaper 4' },
        { id: 5, color: "url('/5.jpg') center / cover no-repeat fixed", name: 'Wallpaper 5' },
        { id: 6, color: "url('/6.jpg') center / cover no-repeat fixed", name: 'Wallpaper 6' },
        { id: 7, color: "url('/7.jpg') center / cover no-repeat fixed", name: 'Wallpaper 7' },
        { id: 8, color: "url('/8.jpg') center / cover no-repeat fixed", name: 'Wallpaper 8' },
        { id: 9, color: "url('/9.jpg') center / cover no-repeat fixed", name: 'Wallpaper 9' },
        { id: 10, color: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)', name: 'Winter Sky' },
        { id: 11, color: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)', name: 'Lavender' },
    ];

    // State Handlers
    const triggerAppUpdate = () => { window.dispatchEvent(new Event('appThemeChanged')); };
    const updateFont = (font) => { setAppFont(font); localStorage.setItem('appFont', font); triggerAppUpdate(); };
    const updateTextSize = (size) => { setAppTextSize(size); localStorage.setItem('appTextSize', size); triggerAppUpdate(); };
    const updateTextColor = (color) => { setAppTextColor(color); localStorage.setItem('appTextColor', color); triggerAppUpdate(); };
    const updateVoiceSettings = (key, val, setter) => { setter(val); localStorage.setItem(key, val); };
    const handleBgChange = (bg) => { 
    setLocalAppBg(bg); 
    localStorage.setItem('appBg', bg); 
    triggerAppUpdate(); 
};
    const handleNotifChange = (val) => { setNotifOn(val); localStorage.setItem('notifOn', JSON.stringify(val)); };

    // Derived Worker Info
    const dojFormatted = parseSafeDate(user.doj);
    const shiftTiming = (user.shift_start && user.shift_end) ? `${formatTimeForDisplay(user.shift_start)} - ${formatTimeForDisplay(user.shift_end)}` : '';
    const loomsFormatted = formatSimpleLoomList(user.looms || user.assigned_looms);
    const totalLoomsCount = (loomsFormatted === 'None' || loomsFormatted === 'Not Assigned') ? 0 : loomsFormatted.split(',').length;
    
    // --- IMPROVED RATE CALCULATION (LIVE DATA SYNC) ---
    const myLiveApiData = (workersData && workersData.find(w => String(w.id) === String(user.id))) || user;
    
    const pendingRate = parseFloat(myLiveApiData.pending_rate) || 0;
    const standardRate = parseFloat(myLiveApiData.rate_per_meter) || parseFloat(myLiveApiData.ratePerMeter) || parseFloat(myLiveApiData.rate) || 0;
    const safeRate = pendingRate > 0 ? pendingRate : standardRate;
    
    const labelStyle = { fontSize: '9px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', letterSpacing: '0.5px' };
    console.log("🔥 Backend Se Aane Wala Data:", user);
   return (
    <div style={{...styles.content, background: localAppBg}}>
        <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght=400;500;600;700;800;900&display=swap');
            
            * { 
              font-family: 'Plus Jakarta Sans', sans-serif; 
              box-sizing: border-box; 
              -webkit-tap-highlight-color: transparent; 
            }

            ::-webkit-scrollbar { width: 4px; height: 4px; }
            ::-webkit-scrollbar-track { background: transparent; }
            ::-webkit-scrollbar-thumb { background: rgba(15, 23, 42, 0.15); border-radius: 10px; }
            
            @keyframes slideUpFade { 0% { opacity: 0; transform: translateY(15px); } 100% { opacity: 1; transform: translateY(0); } }
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            
            .animate-in { animation: slideUpFade 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
            
            .premium-glass {
                background: rgba(255, 255, 255, 0.85) !important;
                backdrop-filter: blur(16px) !important;
                -webkit-backdrop-filter: blur(16px) !important;
                border: 1px solid rgba(255, 255, 255, 0.6) !important;
                box-shadow: 0 10px 30px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,1) !important;
                border-radius: 24px !important;
            }
        `}</style>

       {/* TAB SWITCHER */}
            <Tabs 
                options={[
                    { key: 'profile', label: 'My Profile' },
                    { key: 'settings', label: 'App Settings' }
                ]}
                activeTab={activeTab}
                onChange={(tabKey) => setActiveTab(tabKey)}
                style={{ marginBottom: '20px' }}
            />

            {/* ---------------- PROFILE SECTION ---------------- */}
            {activeTab === 'profile' && (
                <>
                  {/* 1. MY DETAILS SECTION */}
<div className="premium-glass animate-in" style={{...styles.sectionCard, animationDelay: '0.1s'}}>
   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(226, 232, 240, 0.8)', padding: '14px 16px', background: 'rgba(248, 250, 252, 0.5)' }}>                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <IconBox icon={UserCircle} variant="blue" size={14} padding="6px" />
                                <span style={{fontSize: '12px', fontWeight: '900', color: '#0F172A', textTransform: 'uppercase', letterSpacing:'0.5px'}}>My Details</span>
                            </div>
                            <div 
                                onClick={() => { if (editMode.personal) handleSaveProfile(); else toggleEdit('personal'); }} 
                                style={{ cursor: isSaving ? 'not-allowed' : 'pointer', background: editMode.personal ? '#10B981' : '#ffffff', border: editMode.personal ? 'none' : '1px solid #e2e8f0', padding: '4px 10px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s', boxShadow: editMode.personal ? '0 2px 6px rgba(16, 185, 129, 0.2)' : '0 2px 4px rgba(0,0,0,0.02)', opacity: isSaving ? 0.7 : 1 }}
                            >
                                {isSaving ? <span style={{ fontSize: '10px', color: '#fff', fontWeight: '900' }}>...</span> : (editMode.personal ? <ShieldCheck size={12} color="#ffffff" /> : <Edit2 size={12} color="#0F172A" />)}
                                <span style={{ fontSize: '10px', fontWeight: '800', color: editMode.personal ? '#ffffff' : '#0F172A', letterSpacing: '0.5px' }}>{isSaving ? 'SAVING...' : (editMode.personal ? 'SAVE' : 'EDIT')}</span>
                            </div>
                        </div>
                        <div style={{padding: '16px'}}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                <div>
                                    <label style={labelStyle}>Full Name</label>
                                    <input readOnly={!editMode.personal} style={{...inputStyle, background: editMode.personal ? '#ffffff' : 'rgba(255,255,255,0.5)', border: editMode.personal ? '1px solid #4338CA' : '1px solid transparent'}} value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Enter Name" />
                                </div>
                                <div>
                                    <label style={labelStyle}>Mobile Number</label>
                                    <input type="number" readOnly={!editMode.personal} style={{...inputStyle, background: editMode.personal ? '#ffffff' : 'rgba(255,255,255,0.5)', border: editMode.personal ? '1px solid #4338CA' : '1px solid transparent'}} value={formData.mobile} onChange={e => setFormData({...formData, mobile: e.target.value})} placeholder="Enter Mobile" />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div>
                                    <label style={labelStyle}>Aadhaar Number</label>
                                    <input type="number" readOnly={!editMode.personal} style={{...inputStyle, background: editMode.personal ? '#ffffff' : 'rgba(255,255,255,0.5)', border: editMode.personal ? '1px solid #4338CA' : '1px solid transparent'}} value={formData.aadhaar} onChange={e => setFormData({...formData, aadhaar: e.target.value})} placeholder="Enter Aadhaar No." />
                                </div>
                                <div>
                                    <label style={labelStyle}>My Address</label>
                                    <input type="text" readOnly={!editMode.personal} style={{...inputStyle, background: editMode.personal ? '#ffffff' : 'rgba(255,255,255,0.5)', border: editMode.personal ? '1px solid #4338CA' : '1px solid transparent'}} value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="Enter Address" />
                                </div>
                            </div>
                        </div>
                    </div>

                 {/* 2. MY WORK DETAILS SECTION */}
<div className="premium-glass animate-in" style={{...styles.sectionCard, animationDelay: '0.2s'}}>
    <div style={{padding: '14px 16px', background: 'rgba(248, 250, 252, 0.5)', borderBottom: '1px solid rgba(226, 232, 240, 0.8)', display: 'flex', alignItems: 'center', gap: '10px'}}>                            <IconBox icon={Briefcase} variant="green" size={14} padding="6px" />
                            <span style={{fontSize: '12px', fontWeight: '900', color: '#0F172A', textTransform: 'uppercase', letterSpacing:'0.5px'}}>My Work Details</span>
                        </div>
                        <div style={{padding: '0 16px'}}>
                            <ProfileRow icon={Hash} variant="blue" label="Worker ID" value={user.id} onClick={(e) => handleVoiceClick && handleVoiceClick(e, `आपकी वर्कर आई डी ${user.id} है।`)} />
                            <ProfileRow icon={Layers} variant="green" label="Assigned Looms" value={loomsFormatted} onClick={(e) => handleVoiceClick && handleVoiceClick(e, `आपके असाइन किए गए लूम्स हैं: ${loomsFormatted}`)} />
                            <ProfileRow icon={Clock} variant="yellow" label="Shift & Timing" value={`${user.shift || 'N/A'} ${shiftTiming ? `(${shiftTiming})` : ''}`} onClick={(e) => handleVoiceClick && handleVoiceClick(e, getShiftVoiceMsg(user))} />
                            <ProfileRow icon={Banknote} variant="red" label="Rate Per Meter" value={safeRate > 0 ? `₹ ${safeRate} / Meter` : 'Not Set'} onClick={(e) => handleVoiceClick && handleVoiceClick(e, `आपका रेट प्रति मीटर ${safeRate} रुपये है।`)} />
                            <ProfileRow icon={Calendar} variant="dark" label="Date of Joining" value={dojFormatted} onClick={(e) => handleVoiceClick && handleVoiceClick(e, `आपकी जॉइनिंग डेट ${dojFormatted} है।`)} />
                            <ProfileRow icon={Building} variant="blue" label="Work Address" value={user.work_address || 'Textile Factory, Malegaon'} onClick={(e) => handleVoiceClick && handleVoiceClick(e, "यह आपकी फैक्ट्री का पता है।")} />                        </div>
                    </div>

                  {/* 3. MY BANK DETAILS SECTION */}
<div className="premium-glass animate-in" style={{...styles.sectionCard, animationDelay: '0.3s'}}>
   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(226, 232, 240, 0.8)', padding: '14px 16px', background: 'rgba(248, 250, 252, 0.5)' }}>                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <IconBox icon={ShieldCheck} variant="yellow" size={14} padding="6px" />
                                <span style={{fontSize: '12px', fontWeight: '900', color: '#0F172A', textTransform: 'uppercase', letterSpacing:'0.5px'}}>My Bank Details</span>
                            </div>
                            <div 
                                onClick={() => { if (editMode.bank) handleSaveProfile(); else toggleEdit('bank'); }} 
                                style={{ cursor: isSaving ? 'not-allowed' : 'pointer', background: editMode.bank ? '#10B981' : '#ffffff', border: editMode.bank ? 'none' : '1px solid #e2e8f0', padding: '4px 10px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s', boxShadow: editMode.bank ? '0 2px 6px rgba(16, 185, 129, 0.2)' : '0 2px 4px rgba(0,0,0,0.02)', opacity: isSaving ? 0.7 : 1 }}
                            >
                                {isSaving ? <span style={{ fontSize: '10px', color: '#fff', fontWeight: '900' }}>...</span> : (editMode.bank ? <ShieldCheck size={12} color="#ffffff" /> : <Edit2 size={12} color="#0F172A" />)}
                                <span style={{ fontSize: '10px', fontWeight: '800', color: editMode.bank ? '#ffffff' : '#0F172A', letterSpacing: '0.5px' }}>{isSaving ? 'SAVING...' : (editMode.bank ? 'SAVE' : 'EDIT')}</span>
                            </div>
                        </div>

                        <div style={{padding: '16px'}}>
                            
                            <div style={{ marginBottom: '16px' }}>
                                <label style={labelStyle}>Account Number</label>
                                <input 
                                    type="number" 
                                    readOnly={!editMode.bank} 
                                    style={{...inputStyle, background: editMode.bank ? '#ffffff' : 'rgba(255,255,255,0.5)', border: editMode.bank ? '1px solid #4338CA' : '1px solid transparent'}} 
                                    value={formData.bank_account} 
                                    onChange={e => setFormData({...formData, bank_account: e.target.value})} 
                                    placeholder="Enter Account No."
                                />
                            </div>
                            
                            {/* 🚀 STEP 1: SELECT BANK ONLY */}
                            <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                                <label style={{...labelStyle, color: '#111'}}>🏦 1. Select Bank</label>
                                <div style={{ marginTop: '12px' }}>
                                    <select 
                                        disabled={!editMode.bank}
                                        style={{...inputStyle, background: editMode.bank ? '#ffffff' : 'rgba(255,255,255,0.5)', color: '#111', appearance: editMode.bank ? 'auto' : 'none'}}
                                        value={formData.bank_name || ''}
                                        onChange={e => {
                                            setFormData({...formData, bank_name: e.target.value, ifsc_code: ''});
                                            setIfscStatus('idle');
                                            setShowIfscSearch(false); 
                                        }}
                                    >
                                        {formData.bank_name && !INDIAN_BANKS.some(b => b.name === formData.bank_name) && (
                                            <option value={formData.bank_name}>{formData.bank_name}</option>
                                        )}
                                        <option value="">Select Bank...</option>
                                        {INDIAN_BANKS.map((b, i) => <option key={i} value={b.name}>{b.name}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* 🚀 STEP 2: CONNECTED SEARCH UI & FINAL IFSC DISPLAY */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div style={{ position: 'relative' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <label style={{...labelStyle, margin: 0}}>
                                            Exact IFSC Code
                                            {ifscStatus === 'loading' && <span style={{color: '#F59E0B', marginLeft: '6px', fontSize: '9px'}}>Verifying...</span>}
                                        </label>
                                        
                                        <span 
                                            onClick={() => {
                                                if(!editMode.bank) return;
                                                if(!formData.bank_name) { if(handleVoiceClick) handleVoiceClick(null, "पहले बैंक सेलेक्ट करें"); return; }
                                                setShowIfscSearch(!showIfscSearch);
                                            }} 
                                            style={{ fontSize: '9px', fontWeight: '800', color: editMode.bank ? '#2563EB' : '#555', cursor: editMode.bank ? 'pointer' : 'not-allowed', background: 'rgba(255,255,255,0.6)', padding: '4px 8px', borderRadius: '6px', transition: 'all 0.2s' }}
                                        >
                                            {showIfscSearch ? '✖ CLOSE' : '🔍 SEARCH IFSC'}
                                        </span>
                                    </div>

                                    {showIfscSearch && editMode.bank && (
                                        <div style={{ background: 'rgba(255,255,255,0.9)', padding: '10px', borderRadius: '10px', border: '1px solid #93C5FD', marginBottom: '4px', display: 'flex', gap: '6px', flexDirection: 'column', animation: 'fadeIn 0.2s ease' }}>
                                            <select style={{...inputStyle, padding: '8px', fontSize: '11px', background: '#fff'}} value={locState} onChange={e => setLocState(e.target.value)}>
                                                <option value="">Select State...</option>
                                                <option value="Maharashtra">Maharashtra</option>
                                            </select>
                                            <select disabled={!locState} style={{...inputStyle, padding: '8px', fontSize: '11px', background: '#fff'}} value={locDistrict} onChange={e => setLocDistrict(e.target.value)}>
                                                <option value="">Select District...</option>
                                                <option value="Nashik">Nashik</option>
                                                <option value="Dhule">Dhule</option>
                                            </select>
                                            <select 
                                                disabled={!locDistrict} 
                                                style={{...inputStyle, padding: '8px', fontSize: '11px', border: '1px solid #3B82F6', background: '#EFF6FF', color: '#1D4ED8', fontWeight: '900'}} 
                                                onChange={e => {
                                                    const village = e.target.value;
                                                    if(village === 'Malegaon') {
                                                        const foundBank = INDIAN_BANKS.find(b => b.name === formData.bank_name);
                                                        if (foundBank && foundBank.ifsc) {
                                                            handleIfscChange(foundBank.ifsc); 
                                                            setShowIfscSearch(false); 
                                                        } else {
                                                            if(handleVoiceClick) handleVoiceClick(null, "इस बैंक का मालेगांव ब्रांच उपलब्ध नहीं है, कृपया मैन्युअल टाइप करें।");
                                                        }
                                                    }
                                                }}
                                            >
                                                <option value="">Select City/Village...</option>
                                                {locDistrict === 'Nashik' && <option value="Malegaon">Malegaon (Auto-fill)</option>}
                                                {locDistrict === 'Nashik' && <option value="Nashik City">Nashik City</option>}
                                                {locDistrict === 'Dhule' && <option value="Shirpur">Shirpur</option>}
                                            </select>
                                        </div>
                                    )}

                                    {/* Premium Clean Input with Inner Green Tick */}
                                    <div style={{ position: 'relative' }}>
                                        <input 
                                            readOnly={!editMode.bank} 
                                            style={{
                                                ...inputStyle, 
                                                background: editMode.bank ? '#ffffff' : 'rgba(255,255,255,0.5)', 
                                                border: editMode.bank ? (showIfscSearch ? '1px solid #93C5FD' : '1px solid #4338CA') : '1px solid transparent', 
                                                color: '#111', 
                                                textTransform: 'uppercase',
                                                paddingRight: (formData.ifsc_code && formData.ifsc_code.length === 11) ? '36px' : '14px' 
                                            }} 
                                            value={formData.ifsc_code} 
                                            onChange={e => handleIfscChange(e.target.value)} 
                                            placeholder="Enter IFSC Code"
                                            maxLength={11}
                                        />
                                        {formData.ifsc_code && formData.ifsc_code.length === 11 && ifscStatus !== 'invalid' && (
                                            <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
                                                <ShieldCheck size={18} color="#10B981" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                               <div>
                                    <label style={labelStyle}>UPI ID / QR Link</label>
                                    <input 
                                        readOnly={!editMode.bank} 
                                        style={{...inputStyle, background: editMode.bank ? '#ffffff' : 'rgba(255,255,255,0.5)', border: editMode.bank ? '1px solid #4338CA' : '1px solid transparent'}} 
                                        value={formData.upi_id} 
                                        onChange={e => setFormData({...formData, upi_id: e.target.value})} 
                                        placeholder="e.g. xxxxxxxxxx@ybl" 
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ---------------- SETTINGS SECTION ---------------- */}
            {activeTab === 'settings' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    
                  {/* Appearance & Typography */}
<div className="premium-glass animate-in" style={{...styles.sectionCard, animationDelay: '0.1s'}}>
    <div style={{padding: '14px 16px', background: 'rgba(248, 250, 252, 0.5)', borderBottom: '1px solid rgba(226, 232, 240, 0.8)', display: 'flex', alignItems: 'center', gap: '10px'}}>                            <IconBox icon={LayoutTemplate} variant="dark" size={14} padding="6px" />
                            <span style={{fontSize: '12px', fontWeight: '900', color: '#0F172A', textTransform: 'uppercase', letterSpacing:'0.5px'}}>Appearance & Animations</span>
                        </div>
                        <div style={{padding: '16px'}}>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={labelStyle}><Type size={14} /> Global Font Family</label>
                                <div style={{display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px'}}>
                                    {fonts.map(f => (
                                        <button key={f} onClick={() => updateFont(f)} style={{ whiteSpace:'nowrap', background: appFont === f ? '#1A1A1A' : 'rgba(255,255,255,0.4)', color: appFont === f ? 'white' : '#111', padding: '10px 14px', borderRadius: '12px', border: appFont === f ? '1px solid #1A1A1A' : '1px solid rgba(255,255,255,0.3)', fontWeight: '800', fontSize: '12px', cursor: 'pointer', fontFamily: f }}>
                                            {f}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            
                            
                            <div>
                                <label style={labelStyle}><Baseline size={14} /> Text Size Controller</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', background: 'rgba(255,255,255,0.4)', padding: '14px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.3)' }}>
                                    <span style={{ fontSize: '12px', fontWeight: '800', color: '#111' }}>A</span>
                                    <input type="range" min="10" max="16" step="1" value={appTextSize} onChange={(e) => updateTextSize(e.target.value)} style={{ flex: 1, accentColor: '#1A1A1A', cursor: 'pointer' }} />
                                    <span style={{ fontSize: '18px', fontWeight: '900', color: '#111' }}>A</span>
                                    <span style={{ fontSize: '12px', fontWeight: '900', background: '#1A1A1A', color: 'white', padding: '6px 10px', borderRadius: '10px' }}>{appTextSize}px</span>
                                </div>
                            </div>
                        </div>
                    </div>

                   {/* Display & Alerts */}
<div className="premium-glass animate-in" style={{...styles.sectionCard, animationDelay: '0.2s'}}>
    <div style={{padding: '14px 16px', background: 'rgba(248, 250, 252, 0.5)', borderBottom: '1px solid rgba(226, 232, 240, 0.8)', display: 'flex', alignItems: 'center', gap: '10px'}}>                            <IconBox icon={ImageIcon} variant="blue" size={14} padding="6px" />
                            <span style={{fontSize: '12px', fontWeight: '900', color: '#0F172A', textTransform: 'uppercase', letterSpacing:'0.5px'}}>Display & Alerts</span>
                        </div>
                        <div style={{padding: '16px'}}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '14px', background: 'rgba(255,255,255,0.4)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.3)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><div style={{background: 'rgba(255,255,255,0.6)', padding: '8px', borderRadius: '10px', display: 'flex', border: '1px solid rgba(255,255,255,0.3)'}}><Bell size={16} color="#111" /></div><div><div style={{ fontSize: '13px', fontWeight: '900', color: '#111' }}>App Notifications</div><div style={{ fontSize: '10px', color: '#444', fontWeight: '800' }}>Sound & Alerts</div></div></div>
                                <div onClick={() => handleNotifChange(!notifOn)} style={{ width: '48px', height: '26px', background: notifOn ? '#10B981' : '#CBD5E1', borderRadius: '20px', position: 'relative', cursor: 'pointer', transition: 'all 0.3s ease', flexShrink: 0 }}><div style={{ width: '22px', height: '22px', background: 'white', borderRadius: '50%', position: 'absolute', top: '2px', left: notifOn ? '24px' : '2px', transition: 'all 0.3s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} /></div>
                            </div>
                            <div>
                                <label style={labelStyle}>Change Wallpaper (Saves Automatically)</label>
                                <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '10px', padding: '16px', background: 'rgba(255,255,255,0.4)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.3)'}}>
                                    {wallpapers.map(wp => (
                                        <div key={wp.id} onClick={() => handleBgChange(wp.color)} title={wp.name} style={{ width: '36px', height: '36px', borderRadius: '50%', background: wp.color, cursor: 'pointer', border: localAppBg === wp.color ? '3px solid #1A1A1A' : '3px solid white', boxShadow: localAppBg === wp.color ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 6px rgba(0,0,0,0.1)', transition: 'all 0.2s ease', transform: localAppBg === wp.color ? 'scale(1.15)' : 'scale(1)' }} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                  {/* Voice Assistant Setup */}
<div className="premium-glass animate-in" style={{...styles.sectionCard, animationDelay: '0.3s'}}>
    <div style={{padding: '14px 16px', background: 'rgba(248, 250, 252, 0.5)', borderBottom: '1px solid rgba(226, 232, 240, 0.8)', display: 'flex', alignItems: 'center', gap: '10px'}}>                            <IconBox icon={Volume2} variant="red" size={14} padding="6px" />
                            <span style={{fontSize: '12px', fontWeight: '900', color: '#0F172A', textTransform: 'uppercase', letterSpacing:'0.5px'}}>Voice Assistant Setup</span>
                        </div>
                        <div style={{padding: '16px'}}>
                            <label style={labelStyle}>Voice Speed ({voiceSpeed}x)</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
                                <span style={{ fontSize: '11px', fontWeight: '900', color: '#111' }}>Slow</span>
                                <input type="range" min="0.5" max="2.0" step="0.1" value={voiceSpeed} onChange={(e) => updateVoiceSettings('voiceSpeed', e.target.value, setVoiceSpeed)} style={{ flex: 1, accentColor: '#1A1A1A', cursor: 'pointer' }} />
                                <span style={{ fontSize: '11px', fontWeight: '900', color: '#111' }}>Fast</span>
                            </div>

                            <label style={labelStyle}>Select Smart Guide Voice</label>
                            <select style={{width: '100%', padding: '14px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.3)', fontSize: '13px', outline: 'none', background: 'rgba(255,255,255,0.6)', color:'#111', fontWeight:'700'}} value={selectedVoiceURI} onChange={(e) => updateVoiceSettings('selectedVoiceURI', e.target.value, setSelectedVoiceURI)}>
                                <option value="">System Default Voice</option>
                                {availableVoices.map((v, i) => (
                                    <option key={i} value={v.voiceURI}>{v.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* Logout Button (Always at bottom) */}
            <Button 
                onClick={(e) => { 
                    if(window.confirm("Are you sure you want to logout?")) onLogout(e); 
                }} 
                icon={LogOut}
                style={{ width: '100%', padding: '16px', borderRadius: '16px', border: '1px solid #FECACA', background: '#FEF2F2', color: '#DC2626', fontWeight: '900', fontSize: '13px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', marginTop: '10px', marginBottom:'40px' }}
            >
                Log Out from App
            </Button>
        </div>
    );
};

const styles = {
    content: { 
        padding: '24px 16px', 
        maxWidth: '600px', 
        margin: '0 auto', 
        minHeight: '100vh', 
        boxSizing: 'border-box', 
        transition: 'background 0.3s ease',
        position: 'relative',
        zIndex: 1
    },
    sectionCard: { 
        overflow: 'hidden', 
        marginBottom: '24px',
        border: 'none' // premium-glass क्लास इसे खुद हैंडल करेगी
    }
};

export default WorkerProfileView;